import React, { RefObject, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CellRendererProps, FlatList, useWindowDimensions } from 'react-native';
import Animated, {
    Extrapolation,
    clamp,
    interpolate,
    runOnJS,
    useAnimatedReaction,
    useAnimatedStyle,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useRoute } from '@react-navigation/native';

import { EventInfo } from '@/api/events/types/types';
import { EVENT_SHEET_TITLE_HEIGHT } from '@/components/event-sheet/EventSheetTitle';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { SocialFeedEntry } from '@/types/socialFeed.generated';
import { isWeb } from '@/utils/constants-platform-specific';
import { isPGA } from '@/utils/league';
import { BottomSheetFlatList, BottomSheetFlatListMethods, BottomSheetView } from '@gorhom/bottom-sheet';

import { MODAL_HANDLE_HEIGHT } from '../alerts/components/Modal';
import { PlayerWithTeamAndEvent } from '../betslip-pickem/types';
import { useFullScreenPagerTransition } from '../circular-pager/FullScreenPagerTransitionProvider';
import { useScrollableModalContext } from '../circular-pager/ScrollableModalContextProvider';
import { EVENT_HEADER_HERO_IMAGE_HEIGHT, EventHeader } from '../lobby/components/EventHeader';
import { PICK_BAR_HEIGHT } from '../lobby/components/FantasyPickbar';
import { LobbyLeagueComponents, LobbyLeagueSection } from '../lobby/components/LobbyList';
import { StickyCellRendererComponent } from '../lobby/components/sticky-item/StickyCellRendererComponent';
import { useFantasyEventContent } from '../lobby/hooks/use-fantasy-event-content';
import { useOpenDeeplinkPlayer } from '../lobby/hooks/use-open-deeplink-player';
import { usePlayerFiltersScrollControls } from '../lobby/hooks/use-player-filters';
import { StickyWrapper } from '../lobby/hooks/use-sticky-translation';
import { Filters } from '../lobby/utils/filters';
import { MaxWidthWrapper } from '../responsive-design/WebComponents';
import { FantasyEventsSheetsParamsList } from './FantasyEventsSheets';
import { EventDetailsSheetHandle } from './components/EventDetailsSheetHandle';
import { FantasyMatchPreview } from './components/FantasyMatchPreview';
import { PickemEventHud } from './components/PickemEventHud';
import { MatchPreviewResponse } from './hooks/use-match-preview';

type EventBottomSheetPlayersListProps = {
    eventInfo: EventInfo;
    isActive: boolean;
    eventDedicatedHeaderInfo: EventInfo | null;
};

const ListComponent = isWeb ? FlatList : BottomSheetFlatList;

const stickyHeaderHeight = EVENT_HEADER_HERO_IMAGE_HEIGHT - MODAL_HANDLE_HEIGHT - EVENT_SHEET_TITLE_HEIGHT;

type EventListContent = PlayerWithTeamAndEvent | SocialFeedEntry | LobbyLeagueSection | MatchPreviewResponse;

/**
 * The event details list, rendering the event header, player filters and the players list for a selected event.
 */
export const EventDetailsScreen = memo(
    ({ isActive, eventInfo, eventDedicatedHeaderInfo }: EventBottomSheetPlayersListProps) => {
        const { params } = useRoute<FantasyEventsSheetsParamsList['route']>();
        const { listRef, filtersStickPosition, listScrollOffset } = usePlayerFiltersScrollControls();
        const eventDedicatedHeader = isPGA(params.league) ? eventInfo : eventDedicatedHeaderInfo;

        const [shouldRenderPlayers, setShouldRenderPlayers] = useState(false);
        const { sheetMountAnimationProgress } = useFullScreenPagerTransition();

        useAnimatedReaction(
            () => sheetMountAnimationProgress.value,
            current => {
                if (current !== null) {
                    //we don't show the players list when the screen transition/mount starts, for better performance
                    //we only start showing the projection list after 70% of the mount animation is completed
                    if (sheetMountAnimationProgress.value > -0.7 && !shouldRenderPlayers) {
                        runOnJS(setShouldRenderPlayers)(true);
                    }
                }
            }
        );

        const {
            sortedPlayers,
            socialFeedEntries,
            matchPreviewData,
            renderPlayerItem,
            renderSocialFeedItem,
            renderFilters,
            renderLeagueFooter,
            filterType,
        } = useFantasyEventContent({
            league: params.league,
            eventId: eventInfo.id,
            isActive,
        });

        const { scrollOffset, sheetScrollableHook } = useScrollableModalContext();
        useOpenDeeplinkPlayer({
            playerId: params.playerId,
            players: sortedPlayers,
            isTargetEvent: eventInfo.id === params.eventId,
        });

        useAnimatedReaction(
            () => scrollOffset.value,
            current => {
                listScrollOffset.value = current;
            }
        );

        const renderItem = useCallback(
            ({ item, index: itemIndex }: { item: EventListContent; index: number }) => {
                if (typeof item === 'string') {
                    if (item === 'filters') {
                        return (
                            <StickyWrapper initialPosition={filtersStickPosition} scrollOffset={scrollOffset}>
                                <Box backgroundColor={ds.colorToken.background.neutral.base}>{renderFilters()}</Box>
                            </StickyWrapper>
                        );
                    }
                    return null;
                }
                if (isMatchPreview(item)) {
                    return (
                        <MaxWidthWrapper>
                            <Box minHeight={320}>
                                <FantasyMatchPreview league={params.league} matchPreview={matchPreviewData} />
                            </Box>
                        </MaxWidthWrapper>
                    );
                }
                if (isSocialFeedEntry(item)) {
                    return (
                        <Box backgroundColor={isWeb ? ds.colorToken.background.neutral.base : undefined}>
                            {renderSocialFeedItem({
                                item,
                                index: itemIndex,
                                ignoreIndexes: LobbyLeagueComponents.ExpandedEvent.length,
                            })}
                        </Box>
                    );
                }
                return (
                    <Box backgroundColor={isWeb ? ds.colorToken.background.neutral.base : undefined}>
                        {renderPlayerItem({
                            item,
                            index: itemIndex,
                            ignoreIndexes: LobbyLeagueComponents.ExpandedEvent.length,
                        })}
                    </Box>
                );
            },
            [
                filtersStickPosition,
                matchPreviewData,
                params.league,
                renderFilters,
                renderPlayerItem,
                renderSocialFeedItem,
                scrollOffset,
            ]
        );

        // need to memoize the header component to prevent re-renders - flickers on event hud
        const HeaderComponent = useMemo(() => {
            return (
                <MaxWidthWrapper>
                    <PickemEventHud event={eventInfo} />
                </MaxWidthWrapper>
            );
        }, [eventInfo]);

        const renderFooterComponent = useCallback(() => {
            //Do not render the league footer (loading or "No projections available")
            //if the page is not active, to prevent UI jumps
            if (isActive) {
                return renderLeagueFooter();
            } else {
                return null;
            }
        }, [isActive, renderLeagueFooter]);

        const previousIsActive = useRef(isActive);

        const listToRender: EventListContent[] = useMemo(() => {
            if (!shouldRenderPlayers) {
                return [...LobbyLeagueComponents.ExpandedEvent];
            }
            if (filterType === Filters.SocialLineups) {
                return [...LobbyLeagueComponents.ExpandedEvent, ...socialFeedEntries];
            }
            if (filterType === Filters.Preview) {
                if (matchPreviewData) {
                    return [...LobbyLeagueComponents.ExpandedEvent, matchPreviewData];
                } else {
                    return [...LobbyLeagueComponents.ExpandedEvent];
                }
            }
            return [...LobbyLeagueComponents.ExpandedEvent, ...sortedPlayers];
        }, [filterType, matchPreviewData, shouldRenderPlayers, socialFeedEntries, sortedPlayers]);

        const { height } = useWindowDimensions();
        const insets = useSafeAreaInsets();
        const playersTabSpace = filterType === Filters.Position ? 6 : 0;
        const containerStyle = useMemo(() => {
            return {
                // web doesn't have top and bottom insets, so we use a fixed padding so the last item is not cut off
                paddingBottom: (isWeb ? 140 : insets.bottom + insets.top) + 12 + PICK_BAR_HEIGHT + playersTabSpace,
                paddingTop: eventDedicatedHeader
                    ? EVENT_HEADER_HERO_IMAGE_HEIGHT
                    : EVENT_SHEET_TITLE_HEIGHT + MODAL_HANDLE_HEIGHT,
                minHeight: height + (eventDedicatedHeader ? stickyHeaderHeight : 0),
            };
        }, [insets.bottom, insets.top, eventDedicatedHeader, height, playersTabSpace]);

        useEffect(() => {
            if (!isActive && previousIsActive.current) {
                listRef.current?.scrollToOffset({ offset: 0, animated: false });
            }
            previousIsActive.current = isActive;
        }, [eventInfo.name, isActive, listRef]);

        const smoothScrollHeaderStyleBelowTabsPullToRefresh = useAnimatedStyle(() => {
            return {
                transform: [
                    {
                        translateY: clamp(
                            interpolate(
                                scrollOffset.value,
                                [-stickyHeaderHeight, stickyHeaderHeight],
                                [stickyHeaderHeight, -stickyHeaderHeight],
                                Extrapolation.CLAMP
                            ),
                            -Infinity,
                            0
                        ),
                    },
                ],
            };
        });

        const eventHeaderStyle = useAnimatedStyle(() => {
            return {
                opacity: interpolate(
                    scrollOffset.value,
                    [0, (stickyHeaderHeight * 3) / 4],
                    [0, 1],
                    Extrapolation.CLAMP
                ),
            };
        });

        // Using a custom cell renderer component to make sure the filters are rendered above the other items
        // because we stick the filters to the top of the screen
        // and we need to make sure the other items are rendered below it
        const CellRendererComponent = useCallback(
            ({ children, style, ...props }: CellRendererProps<EventListContent>) => {
                const renderingFilters = props.cellKey === 'filters';
                const adjustedStyle = {
                    style,
                    zIndex: renderingFilters ? 100 : undefined,
                    top: 0,
                };
                // StickyCellRendererComponent doesn't work properly on web, only part of the player list is rendering
                // Best solution for now is to remove the sticky tabs on web (players/projections)
                return isWeb ? (
                    <Box style={style} {...props}>
                        {children}
                    </Box>
                ) : (
                    <StickyCellRendererComponent
                        {...props}
                        shouldBeSticky={renderingFilters}
                        style={adjustedStyle}
                        onLayout={event => {
                            if (renderingFilters) {
                                // Projection / Position filters should stick below the event sheet title
                                filtersStickPosition.value =
                                    event.nativeEvent.layout.y - MODAL_HANDLE_HEIGHT - EVENT_SHEET_TITLE_HEIGHT;
                            }
                            props.onLayout?.(event);
                        }}
                    >
                        {children}
                    </StickyCellRendererComponent>
                );
            },
            [filtersStickPosition]
        );

        return (
            <Box flex={1} minHeight={'100%'} backgroundColor={ds.colorToken.background.neutral.base}>
                <EventDetailsSheetHandle eventInfo={eventInfo} eventDedicatedHeaderInfo={eventDedicatedHeader} />
                <MaxWidthWrapper>
                    <Box zIndex={-1}>
                        {eventDedicatedHeader ? (
                            <Box position={'absolute'} width={'100%'} zIndex={-1} overflow={'hidden'}>
                                <Animated.View style={smoothScrollHeaderStyleBelowTabsPullToRefresh}>
                                    <EventHeader
                                        singleEvent={eventDedicatedHeader}
                                        headerOverlayStyle={eventHeaderStyle}
                                    />
                                </Animated.View>
                            </Box>
                        ) : null}
                    </Box>
                </MaxWidthWrapper>
                <Box flex={1} minHeight={'100%'}>
                    <ListComponent
                        ListHeaderComponent={HeaderComponent}
                        ListEmptyComponent={ListEmptyComponent}
                        ListFooterComponent={renderFooterComponent}
                        data={listToRender}
                        windowSize={3}
                        maxToRenderPerBatch={10}
                        keyExtractor={keyExtractor}
                        renderItem={renderItem}
                        CellRendererComponent={CellRendererComponent}
                        contentContainerStyle={containerStyle}
                        ref={listRef as RefObject<BottomSheetFlatListMethods>}
                        showsVerticalScrollIndicator={false}
                        scrollEventsHandlersHook={sheetScrollableHook}
                        // needed for sticky components to work on Android
                        removeClippedSubviews={false}
                    />
                </Box>
            </Box>
        );
    }
);

EventDetailsScreen.displayName = 'EventDetailsScreen';

// mock empty component for loading state
export const ListEmptyComponent = () => {
    const { height } = useWindowDimensions();
    return (
        <BottomSheetView>
            <Row justifyContent={'center'} paddingVertical={'s20'} height={height}>
                <Box />
            </Row>
        </BottomSheetView>
    );
};

const isSocialFeedEntry = (item: EventListContent): item is SocialFeedEntry =>
    typeof item === 'object' && 'socialFeedEntryId' in item;

const isMatchPreview = (item: EventListContent): item is MatchPreviewResponse =>
    typeof item === 'object' && 'headline' in item;

const keyExtractor = (item: EventListContent, index: number) => {
    if (typeof item === 'string') {
        return `${item}`;
    }
    if (isSocialFeedEntry(item)) {
        return `social-feed-${item.socialFeedEntryId}`;
    }
    if (isMatchPreview(item)) {
        return `match-preview-${item.headline}`;
    }
    return `player-${item.id}-${index}`;
};
